import * as Sentry from "@sentry/browser";
import { z } from "zod";

import { setWithExpiry, getWithExpiry } from "app/utils/sessionStorage";
import { validateSchema } from "app/utils/validateSchema";

import type { AppConfig } from "app/contexts/schema";
import type { Headers } from "react-relay-network-modern/lib";

export const SamlProviderSchema = z.object({
  name: z.string(),
  idp_id: z.string(),
  display_name: z.string().optional(),
});

export const SamlProvidersArraySchema = z.array(SamlProviderSchema);

export type SamlProvider = z.infer<typeof SamlProviderSchema>;

/** One service's endpoints, as `application.json` carries them. */
interface ServiceBlock {
  /** Absolute URL of the service, with no trailing slash. */
  url: string;
}

export interface Configs {
  cognito: {
    cognito_install: string;
    cognito_user_pool_region: string;
    cognito_user_pool_id: string;
    cognito_user_pool_client_id: string;
    /**
     * Where a federated IdP posts its SAML response: Cognito's assertion
     * consumer service. An operator configuring a SAML provider pastes it into
     * that provider, so the console shows it rather than sending them looking.
     * Optional because the console ships separately from the platform, and a
     * `cognito.json` that predates the key carries neither this nor
     * `cognito_sp_entity_id`.
     */
    cognito_acs_url?: string;
    /**
     * The audience a federated IdP must restrict its assertion to: the user
     * pool's service-provider entity ID. Shown beside `cognito_acs_url`, and
     * absent under the same conditions.
     */
    cognito_sp_entity_id?: string;
    saml: Record<string, string>;
    saml_providers?: SamlProvider[];
  };

  /**
   * The platform API, and the only block that is not optional: a console that
   * does not know where the API is cannot do anything, so its absence fails the
   * whole read rather than yielding a config the console boots on.
   */
  api: {
    url: string;
    /**
     * Extra headers every GraphQL request carries. Only a legacy
     * `deployment.json` sets these — `application.json`'s `api` block has no
     * such key — so this is empty on any deployment serving the blocks.
     */
    headers?: Headers;
    /**
     * Local-dev only (`just local-console`): where the console mints a JWT from
     * the local IdP. Vite serves it as a same-origin path it proxies
     * (`/api/local-token`), so the IdP sees a loopback peer even when the
     * browser is on another machine. The platform never sets it.
     */
    local_idp_token_url?: string;
  };

  cube?: ServiceBlock;

  docs?: ServiceBlock;

  jun0?: ServiceBlock & {
    /**
     * The hosted MCP endpoint. Served by the platform API rather than by jun0,
     * but grouped here as the console's other AI-facing surface, and absent on
     * a deployment that runs no hosted MCP.
     */
    mcp_url?: string;
  };

  redash?: ServiceBlock;

  sinistral?: ServiceBlock;

  superset?: ServiceBlock;
}

/** Every key of `Configs` that `application.json`'s service blocks provide. */
type ServiceConfigs = Omit<Configs, "cognito">;

const CONFIG_TTL_MINUTES = 60;

// A response that says not to keep it still gets a few seconds. That is short
// enough that the next use of a config re-checks the file, and long enough that
// the several reads of one page load do not each fetch it again.
const UNKEEPABLE_TTL_MINUTES = 5 / 60;

const CognitoSchema = z.object({
  cognito_install: z.string(),
  cognito_user_pool_region: z.string(),
  cognito_user_pool_id: z.string(),
  cognito_user_pool_client_id: z.string(),
  cognito_acs_url: z.string().optional(),
  cognito_sp_entity_id: z.string().optional(),
  saml: z.record(z.string()),
  saml_providers: SamlProvidersArraySchema.optional(),
});

/**
 * A URL a service block carries.
 *
 * Empty is not a URL. The superseded files use one to say a service is not
 * running, and `_blockFor` reads it that way; the blocks say the same thing by
 * leaving the service out. Rejecting it here keeps the two agreeing, and keeps
 * `_asUrl` from turning it into the bare scheme `https://`.
 */
const UrlSchema = z.string().min(1);

const ServiceBlockSchema = z.object({ url: UrlSchema });

/**
 * An optional service block, where a malformed one reads as an absent one.
 *
 * A block the console cannot parse costs one dead link. Failing the whole read
 * over it would cost far more: the fallback below is the only other answer, and
 * it is a path that stops existing once the superseded files are deleted. So a
 * bad block is dropped and reported — see `_warnAboutDroppedBlocks` — and `api`
 * is left as the only block that can fail the read.
 */
function _optionalBlock<S extends z.ZodTypeAny>(
  schema: S,
): z.ZodCatch<z.ZodOptional<S>> {
  return schema.optional().catch(() => undefined);
}

/** Every optional service, in the order `Configs` declares them. */
const OPTIONAL_SERVICES = [
  "cube",
  "docs",
  "jun0",
  "redash",
  "sinistral",
  "superset",
] as const;

/**
 * The service blocks of `application.json`.
 *
 * Parsing against this is also how the console tells a platform that serves the
 * blocks from one that does not. `api` is the whole signal: a platform that
 * serves the blocks always carries it, and an older `application.json` carries
 * no `api` key at all — it names most of the other services, but as a bare URL
 * string rather than an object. Those bare strings are dropped by
 * `_optionalBlock` rather than failing the parse, so `api` alone decides, and
 * one malformed block on a current platform cannot be mistaken for an old one.
 * See `_loadServices`.
 *
 * The `satisfies` names `unknown` as the input type because `_optionalBlock`
 * widens it to that — accepting anything is the point of it. The output is the
 * half that has to match `Configs`.
 */
const ServiceBlocksSchema = z.object({
  api: z.object({
    url: UrlSchema,
    headers: z.record(z.string()).optional(),
    // Not `UrlSchema`: a path rather than a URL, never normalised by `_asUrl`,
    // and both readers already treat "" as absent. A `min(1)` would promote an
    // empty one into a failed `api` block, which now throws -- a console that
    // will not boot, in place of "is this local-dev?".
    local_idp_token_url: z.string().optional(),
  }),
  cube: _optionalBlock(ServiceBlockSchema),
  docs: _optionalBlock(ServiceBlockSchema),
  jun0: _optionalBlock(
    ServiceBlockSchema.extend({
      // Its own `catch`, so that an unusable MCP endpoint costs the MCP
      // endpoint and not the whole jun0 block.
      mcp_url: UrlSchema.optional().catch(() => undefined),
    }),
  ),
  redash: _optionalBlock(ServiceBlockSchema),
  sinistral: _optionalBlock(ServiceBlockSchema),
  superset: _optionalBlock(ServiceBlockSchema),
}) satisfies z.ZodType<ServiceConfigs, z.ZodTypeDef, unknown>;

const ConfigsSchema = ServiceBlocksSchema.extend({
  cognito: CognitoSchema,
}) satisfies z.ZodType<Configs, z.ZodTypeDef, unknown>;

/**
 * `application.json`: the console's deployment flags, plus a block per service.
 *
 * The flags are `AppConfig`'s business — `app/contexts/schema` declares them,
 * and the platform allow-lists exactly that list before serving them. This
 * module reads the same file only for the service blocks.
 */
export type ApplicationConfig = AppConfig & Partial<ServiceConfigs>;

/**
 * The single-key files that `application.json`'s blocks supersede, together.
 *
 * Deprecated by the platform on 2026-09-17 and still served, so that a console
 * release can land on a deployment whose platform predates the blocks. Delete
 * this and `_servicesFromFiles` once no supported platform release serves them
 * — at which point `_loadServices` loses its fallback and `application.json` is
 * the only file left besides `cognito.json`.
 *
 * One schema over all seven, so the fallback path is parsed once the way the
 * blocks are, rather than cast and hoped for. The optional files catch their
 * own errors, as the optional blocks do; the three required ones do not,
 * because `_blockFor` and `_asUrl` are written for strings and a value that is
 * not one reaches them as `undefined.includes is not a function`, thrown from a
 * helper that cannot say which file was wrong.
 */
const LegacyFilesSchema = z.object({
  cubejs: z.object({ cubejs_domain: z.string() }),
  deployment: z.object({
    gql_uri: z.string(),
    headers: z.record(z.string()).optional(),
    local_idp_token_url: z.string().optional(),
  }),
  redash: z.object({ redash_uri: z.string() }),
  docs: _optionalBlock(z.object({ docs_url: z.string() })),
  jun0: _optionalBlock(z.object({ jun0_url: z.string() })),
  sinistral: _optionalBlock(z.object({ sinistral_url: z.string() })),
  superset: _optionalBlock(z.object({ superset_uri: z.string() })),
});

/**
 * Fetches one config file, and reports how long its response may be kept.
 *
 * A 404 is an answer, not a failure: the server states that the file does not
 * exist, so this returns `undefined` for the value. A body of `null` reads the
 * same way, which keeps a fresh read and a cached read in agreement. Any other
 * error response throws, because the server did not state anything about the
 * file.
 */
async function _fetchConfig<T>(
  name: string,
): Promise<{ value: T | undefined; ttlMinutes: number }> {
  const response = await fetch(`/config/${name}.json`);
  if (!response.ok && response.status !== 404) {
    throw new Error(`Unable to fetch /config/${name}.json: ${response.status}`);
  }
  return {
    value: response.ok ? ((await response.json()) ?? undefined) : undefined,
    ttlMinutes: _ttlMinutesFor(response.headers.get("cache-control")),
  };
}

/**
 * Reads a `Cache-Control` header, and reports how long to keep the response.
 *
 * The server decides. A response that says not to keep it gets seconds instead
 * of an hour, whether it carries a file or a 404: the server knows whether the
 * file is settled, and the console does not second-guess it.
 *
 * Directive names are matched without case, because a server may send any case.
 * `max-age` is read only far enough to tell a zero from every other value.
 */
function _ttlMinutesFor(cacheControl: string | null): number {
  const directives = (cacheControl ?? "")
    .toLowerCase()
    .split(",")
    .map((directive) => directive.trim());
  const isUnkeepable = directives.some(
    (directive) =>
      directive === "no-cache" ||
      directive === "no-store" ||
      /^max-age\s*=\s*0+$/.test(directive),
  );
  return isUnkeepable ? UNKEEPABLE_TTL_MINUTES : CONFIG_TTL_MINUTES;
}

/**
 * Reads one config file, from the session cache when it holds a fresh copy.
 *
 * Each file is cached under its own key, so one short-lived file does not hold
 * back the rest, and the response decides how long its entry lives. A response
 * that `_fetchConfig` rejects never enters the cache.
 *
 * `cacheAbsence` states whether an absent file is a valid answer to keep. For a
 * file that every environment must have, a 404 means a broken deployment, and a
 * cached 404 would keep the console broken for the whole TTL.
 *
 * `sessionCache` turns the cache off entirely, for a file that has a better one.
 * Session storage outlives a reload, so a file cached here is a file whose new
 * contents a reload does not pick up.
 *
 * The cache is an optimization, so neither a read nor a write of it may change
 * the result. Session storage rejects both when it is full, and when the browser
 * blocks storage.
 */
async function _readConfig<T>(
  name: string,
  {
    cacheAbsence,
    sessionCache = true,
  }: { cacheAbsence: boolean; sessionCache?: boolean },
): Promise<T | undefined> {
  const key = `config:${name}`;
  try {
    const cached = sessionCache ? getWithExpiry(key) : null;
    if (cached !== null) {
      return JSON.parse(cached) ?? undefined;
    }
  } catch (e) {
    console.warn(`Unable to read the cached /config/${name}.json`, e);
  }

  const { value, ttlMinutes } = await _fetchConfig<T>(name);
  if (sessionCache && (value !== undefined || cacheAbsence)) {
    try {
      setWithExpiry(key, JSON.stringify(value ?? null), ttlMinutes);
    } catch (e) {
      console.warn(`Unable to cache /config/${name}.json`, e);
    }
  }
  return value;
}

/** Reads a config file that every environment must have. */
async function _getConfig<T = unknown>(
  name: string,
  { sessionCache = true }: { sessionCache?: boolean } = {},
): Promise<T> {
  const value = await _readConfig<T>(name, {
    cacheAbsence: false,
    sessionCache,
  });
  if (value === undefined) {
    throw new Error(`Missing required config /config/${name}.json`);
  }
  return value;
}

/**
 * Reads a config file that may be absent in some environments.
 *
 * A failure is not worth a report: the file is optional, and the server logs
 * its own errors.
 */
async function _getOptionalConfig<T = unknown>(
  name: string,
): Promise<T | undefined> {
  try {
    return await _readConfig<T>(name, { cacheAbsence: true });
  } catch {
    return undefined;
  }
}

let applicationPromise: Promise<ApplicationConfig> | null = null;

/** Exposed for test isolation only — resets the cache and the warn-once flags. */
export function resetApplicationConfigCache() {
  applicationPromise = null;
  warnedAboutLegacyConfig = false;
  warnedAboutDroppedBlocks = false;
}

/**
 * Reads `application.json`, once for the life of the page.
 *
 * Both readers of this file share the promise: the flags reach components
 * through `ConfigContext`, and the service blocks reach the rest of the console
 * through `getConfigs`. The promise is what keeps that to one request, and it is
 * the only cache this file gets: `resolveGqlUri` reads it on every GraphQL
 * request, which the promise answers without touching storage, while a reload
 * re-reads the file. The deployment flags ride in here, and a flag that a
 * reload does not pick up is a flag an operator cannot turn off.
 *
 * A failed read is not memoized, so the next caller tries again.
 */
export function getApplicationConfig(): Promise<ApplicationConfig> {
  applicationPromise ??= _getConfig<ApplicationConfig>("application", {
    sessionCache: false,
  }).catch((e) => {
    applicationPromise = null;
    throw e;
  });
  return applicationPromise;
}

/**
 * Absolute form of a value a source may publish as a bare domain.
 *
 * Only cubejs does that (`cubejs.example.com`). A value that already names a
 * scheme, and a path-relative one, pass through. Mirrors `_as_url` in the
 * platform's config endpoints, which normalises the same value for `cube.url`.
 */
function _asUrl(value: string): string {
  return value.includes("://") || value.startsWith("/")
    ? value
    : `https://${value}`;
}

/**
 * One service block built from a legacy file's URL, or `undefined`.
 *
 * An empty string counts as absent: local-dev serves one for a backend that is
 * not running, and the blocks say the same thing by leaving the service out.
 */
function _blockFor(url: string | undefined): ServiceBlock | undefined {
  return url ? { url: _asUrl(url) } : undefined;
}

/**
 * The same services, with every URL in absolute form.
 *
 * The platform normalises these before it serves them, so this changes nothing
 * on a well-behaved deployment. It runs anyway because the consumers cannot
 * tell a bare domain from a mistake: `useCubejsConfig` reads anything that is
 * not absolute as path-relative, and would build `wss://<console-host><domain>`
 * out of one. Absolute-or-path-relative is the contract they are written to,
 * and this is where it is met — for the blocks as `_blockFor` already meets it
 * for the superseded files.
 *
 * Every URL means `jun0.mcp_url` too, which is the one the console does not use
 * itself: it hands it to an MCP client to connect with, and a bare domain is no
 * more usable there than in a `url`.
 */
function _normalised(services: ServiceConfigs): ServiceConfigs {
  const block = <B extends ServiceBlock>(value: B | undefined) =>
    value && { ...value, url: _asUrl(value.url) };
  const jun0 = block(services.jun0);
  return {
    api: { ...services.api, url: _asUrl(services.api.url) },
    cube: block(services.cube),
    docs: block(services.docs),
    jun0: jun0?.mcp_url ? { ...jun0, mcp_url: _asUrl(jun0.mcp_url) } : jun0,
    redash: block(services.redash),
    sinistral: block(services.sinistral),
    superset: block(services.superset),
  };
}

let warnedAboutLegacyConfig = false;
let warnedAboutDroppedBlocks = false;

/**
 * Reports any block `application.json` names that failed to parse.
 *
 * `_optionalBlock` drops those silently so that one bad block does not fail the
 * read, which leaves this as the only sign it happened. Once per page, because
 * `getConfigs` runs on every GraphQL request.
 */
function _warnAboutDroppedBlocks(
  application: ApplicationConfig,
  services: ServiceConfigs,
) {
  const dropped = OPTIONAL_SERVICES.filter(
    (name) => application[name] !== undefined && services[name] === undefined,
  );
  if (dropped.length && !warnedAboutDroppedBlocks) {
    warnedAboutDroppedBlocks = true;
    console.warn(
      `Ignoring malformed service blocks in /config/application.json: ${dropped.join(", ")}. ` +
        "Those services are treated as not deployed.",
    );
  }
}

/**
 * Reads every service's endpoints, preferring `application.json`'s blocks.
 *
 * The blocks are one read that already happened, so the fallback below is the
 * only path that costs requests — seven of them, one per service. It exists
 * because the console ships separately from the platform service: a release can
 * land on a deployment whose `application.json` predates the blocks, and reading
 * the superseded files is what keeps that console booting rather than failing on
 * a missing `api.url`.
 *
 * An *absent* `api` key is the only thing that takes it. A present one the
 * console cannot read means a platform that serves the blocks and got them
 * wrong, and the superseded files it also serves are deprecated — a deployment
 * that moves its API has no reason to keep them in step. Reading them there
 * would trade a loud failure for a quiet wrong endpoint, so that case throws.
 * See `ServiceBlocksSchema` for why one bad *optional* block does neither.
 */
async function _loadServices(
  application: ApplicationConfig,
): Promise<ServiceConfigs> {
  const [services, errors] = validateSchema(ServiceBlocksSchema, application);
  if (services) {
    _warnAboutDroppedBlocks(application, services);
    return _normalised(services);
  }

  // Only `api` can have failed: every other block catches its own errors.
  if (application.api !== undefined) {
    const detail = (errors ?? [])
      .map((issue) => `${issue.path.join(".")}: ${issue.message}`)
      .join("; ");
    throw new Error(
      `Malformed api block in /config/application.json: ${detail}`,
    );
  }

  if (!warnedAboutLegacyConfig) {
    warnedAboutLegacyConfig = true;
    console.info(
      "No service blocks in /config/application.json; reading the superseded " +
        "per-service files. This deployment's platform predates them.",
    );
  }
  return _servicesFromFiles();
}

/**
 * Reads every service's endpoints from the file the platform serves per service.
 *
 * Superseded by `application.json`'s blocks; see `LegacyFileSchemas`. The one
 * difference the callers can see is `sinistral`, which these files name on every
 * deployment while the block is left out where sinistral was never deployed.
 */
async function _servicesFromFiles(): Promise<ServiceConfigs> {
  const [cubejs, deployment, docs, jun0, redash, sinistral, superset] =
    await Promise.all([
      _getConfig("cubejs"),
      _getConfig("deployment"),
      // Every consumer of the docs URL already handles its absence.
      _getOptionalConfig("docs"),
      // Jun0 may not have config yet, which is ok.
      _getOptionalConfig("jun0"),
      _getConfig("redash"),
      // Sinistral may not be deployed in this environment, which is ok.
      _getOptionalConfig("sinistral"),
      // Superset may not be deployed in this environment, which is ok.
      _getOptionalConfig("superset"),
    ]);
  const [files, errors] = validateSchema(LegacyFilesSchema, {
    cubejs,
    deployment,
    docs,
    jun0,
    redash,
    sinistral,
    superset,
  });
  if (!files) {
    const detail = (errors ?? [])
      .map(({ path, message }) => {
        const [file, ...field] = path;
        const where = field.length ? ` (${field.join(".")})` : "";
        return `/config/${String(file)}.json${where}: ${message}`;
      })
      .join("; ");
    throw new Error(`Malformed superseded config file: ${detail}`);
  }
  return {
    api: {
      url: files.deployment.gql_uri,
      headers: files.deployment.headers,
      local_idp_token_url: files.deployment.local_idp_token_url,
    },
    cube: _blockFor(files.cubejs.cubejs_domain),
    docs: _blockFor(files.docs?.docs_url),
    // No file carries mcp_url: it arrived with the blocks and has no legacy
    // equivalent, so hosted MCP stays unavailable on a platform this old.
    jun0: _blockFor(files.jun0?.jun0_url),
    redash: _blockFor(files.redash.redash_uri),
    sinistral: _blockFor(files.sinistral?.sinistral_url),
    superset: _blockFor(files.superset?.superset_uri),
  };
}

async function _loadConfigs(): Promise<Configs> {
  const [cognito, application] = await Promise.all([
    _getConfig<Configs["cognito"]>("cognito"),
    getApplicationConfig(),
  ]);
  const configs: Configs = { cognito, ...(await _loadServices(application)) };
  // Warn only: a slightly malformed config should not take down the console. A
  // malformed file is a deployment fault, which the server that serves it logs.
  // Every service block reached this already parsed, on either path, so what
  // is left for this to catch is a bad cognito.json.
  const [, validationErrors] = validateSchema(ConfigsSchema, configs);
  if (validationErrors) {
    console.warn("Fetched configs failed validation:", validationErrors);
  }
  return configs;
}

let inflightConfigs: Promise<Configs> | null = null;

const configsListeners = new Set<(configs: Configs) => void>();

/**
 * Registers a callback for every successful read of the configs, and returns a
 * function that removes it again.
 *
 * An optional file that failed one read may succeed on the next one. A caller
 * that copies the configs elsewhere, such as the Relay store, needs to hear
 * about that recovery to stay in step. Only the fallback path can recover that
 * way — the blocks arrive whole, in a file that is read once — so this goes
 * quiet along with `_servicesFromFiles`.
 */
export function onConfigsLoaded(
  listener: (configs: Configs) => void,
): () => void {
  configsListeners.add(listener);
  return () => configsListeners.delete(listener);
}

/**
 * Reads the config files for this deployment.
 *
 * Concurrent callers share one read, and the promise is dropped once it settles.
 * `application.json` is memoized for the life of the page, and the session cache
 * is the only cache that outlives a call for the rest, so a file that the server
 * says not to keep is read again on the next call, and a file that failed gets
 * another chance. Sequential callers do not share a read: `RelayEnvironment`
 * shares one per request itself, because its two middlewares run in turn.
 */
export function getConfigs(): Promise<Configs> {
  inflightConfigs ??= _loadConfigs()
    .then((configs) => {
      configsListeners.forEach((listener) => {
        // A listener must never fail the read that told it the news. A listener
        // that throws is a fault in the console itself, so it gets reported.
        try {
          listener(configs);
        } catch (e) {
          console.warn("A config listener failed", e);
          Sentry.captureException(e);
        }
      });
      return configs;
    })
    .finally(() => {
      inflightConfigs = null;
    });
  return inflightConfigs;
}
